// src/components/Header.js
import React from "react";
import { Link } from "react-router-dom";
import { TRANSLATIONS, BOOKING_TRANSLATIONS } from "../data/translations";
import "../styles/Header.css";

// Navigation Dropdown Component
const NavigationDropdown = React.memo(({ t }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nav-dropdown')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  return (
    <div className="nav-dropdown">
      <button 
        className={`nav-dropdown-btn ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="hamburger-icon">
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>
      
      {isOpen && (
        <div className="nav-dropdown-menu">
          <Link 
            to="/" 
            className="nav-dropdown-link"
            onClick={() => setIsOpen(false)}
          >
            {t.nav.home}
          </Link>
          <Link 
            to="/gallery" 
            className="nav-dropdown-link"
            onClick={() => setIsOpen(false)}
          >
            {t.nav.gallery}
          </Link>
        </div>
      )}
    </div>
  );
});

const LanguageDropdown = React.memo(({ currentLang, onLanguageChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.language-dropdown')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleLanguageSelect = (lang) => {
    onLanguageChange(lang);
    setIsOpen(false);
  };

  return (
    <div className="language-dropdown">
      <button 
        className={`language-dropdown-btn ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {currentLang.toUpperCase()} <span className="dropdown-arrow">▼</span>
      </button>
      
      {isOpen && (
        <div className="language-dropdown-menu">
          <button 
            onClick={() => handleLanguageSelect('fr')}
            className={currentLang === 'fr' ? 'active' : ''}
          >
            FR
          </button>
          <button 
            onClick={() => handleLanguageSelect('en')}
            className={currentLang === 'en' ? 'active' : ''}
          >
            EN
          </button>
          <button 
            onClick={() => handleLanguageSelect('de')}
            className={currentLang === 'de' ? 'active' : ''}
          >
            DE
          </button>
        </div>
      )}
    </div>
  );
});

// NOUVEAU: Widget Lodgify Modal Component
const LodgifyBookingModal = React.memo(({ isOpen, onClose, language }) => {
  React.useEffect(() => {
    if (isOpen) {
      // Charger le script Lodgify quand la modal s'ouvre
      const script = document.createElement('script');
      script.src = 'https://app.lodgify.com/book-now-box/stable/renderBookNowBox.js';
      script.defer = true;
      script.onload = () => {
        console.log('Lodgify script loaded');
      };
      document.head.appendChild(script);

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script);
        }
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const t = BOOKING_TRANSLATIONS[language] || BOOKING_TRANSLATIONS.fr;

  // Styles CSS pour le widget Lodgify
  const lodgifyStyles = `
    :root {
      --ldg-bnb-background: #ffffff;
      --ldg-bnb-border-radius: 0;
      --ldg-bnb-box-shadow: none;
      --ldg-bnb-padding: 20px;
      --ldg-bnb-input-background: #ffffff;
      --ldg-bnb-button-border-radius: 0;
      --ldg-bnb-color-primary: #2c2c2c;
      --ldg-bnb-color-primary-lighter: #5a5a5a;
      --ldg-bnb-color-primary-darker: #1a1a1a;
      --ldg-bnb-color-primary-contrast: #ffffff;
      --ldg-component-calendar-cell-selection-bg-color: #2c2c2c;
      --ldg-component-calendar-cell-selection-color: #ffffff;
      --ldg-component-calendar-cell-selected-bg-color: #5a5a5a;
      --ldg-component-calendar-cell-selected-color: #ffffff;
      --ldg-bnb-font-family: "Helvetica Neue", Arial, sans-serif;
    }
    #lodgify-book-now-box {    
      width: 100%;
      border: none;
      border-radius: 0;
      background: transparent;
    }
    .lodgify-modal-content {
      max-width: 600px;
      width: 90%;
      max-height: 90vh;
      overflow: auto;
    }
  `;

  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div className="booking-modal lodgify-modal-content" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="booking-modal-close">
          ×
        </button>

        <div className="booking-modal-header">
          <h2 className="booking-modal-title">{t.title}</h2>
          <p className="booking-modal-subtitle">Réservation directe avec calendrier en temps réel</p>
        </div>

        {/* Widget Lodgify */}
        <div className="lodgify-widget-container">
          <style dangerouslySetInnerHTML={{ __html: lodgifyStyles }} />
          
          <div
            id="lodgify-book-now-box"
            data-rental-id="700978"
            data-website-id="601073"
            data-slug="juralodgespa"
            data-language-code={language}
            data-new-tab="true"
            data-version="stable"
            data-currency-code="CHF"
            
            data-check-in-label={language === 'fr' ? 'Arrivée' : language === 'en' ? 'Check-in' : 'Anreise'}
            data-check-out-label={language === 'fr' ? 'Départ' : language === 'en' ? 'Check-out' : 'Abreise'}
            data-guests-label={language === 'fr' ? 'Invités' : language === 'en' ? 'Guests' : 'Gäste'}
            data-guests-singular-label={
              language === 'fr' ? '{{NumberOfGuests}} invité' : 
              language === 'en' ? '{{NumberOfGuests}} guest' : 
              '{{NumberOfGuests}} Gast'
            }
            data-guests-plural-label={
              language === 'fr' ? '{{NumberOfGuests}} invités' : 
              language === 'en' ? '{{NumberOfGuests}} guests' : 
              '{{NumberOfGuests}} Gäste'
            }
            data-book-button-label={
              language === 'fr' ? 'Réservez maintenant' : 
              language === 'en' ? 'Book now' : 
              'Jetzt buchen'
            }
            data-total-price-label={language === 'fr' ? 'Prix total :' : language === 'en' ? 'Total price:' : 'Gesamtpreis:'}
            data-select-dates-to-see-price-label={
              language === 'fr' ? 'Sélectionnez les dates pour voir le prix total' : 
              language === 'en' ? 'Select dates to see total price' : 
              'Daten auswählen um Gesamtpreis zu sehen'
            }
            data-minimum-price-per-night-first-label={language === 'fr' ? 'À partir de' : language === 'en' ? 'From' : 'Ab'}
            data-minimum-price-per-night-second-label={language === 'fr' ? 'par nuit' : language === 'en' ? 'per night' : 'pro Nacht'}
            data-adults-label='{"one":"adulte","other":"adultes"}'
            data-adults-description='Âges {minAge} ou plus'
            data-children-label='{"one":"enfant","other":"enfants"}'
            data-children-description='Âges {minAge} - {maxAge}'
            data-children-not-allowed-label='Non adapté aux enfants'
            data-infants-label='{"one":"bébé","other":"bébés"}'
            data-infants-description='Moins de {maxAge}'
            data-infants-not-allowed-label='Non adapté aux bébés'
            data-pets-label='{"one":"animal de compagnie","other":"animaux de compagnie"}'
            data-pets-not-allowed-label='Non autorisé'
            data-done-label='Terminé'
          />
        </div>

        {/* Liens alternatifs en bas */}
        <div className="alternative-booking-links">
          <p style={{ fontSize: '12px', color: '#9c9c9c', textAlign: 'center', margin: '20px 0 10px 0' }}>
            Ou réservez via :
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
            <a
              href="https://www.booking.com/Share-pIp0GV"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '12px', color: '#2c2c2c', textDecoration: 'none' }}
            >
              Booking.com
            </a>
            <a
              href="http://airbnb.com/h/studiodelemont"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '12px', color: '#2c2c2c', textDecoration: 'none' }}
            >
              Airbnb
            </a>
            <a
              href="mailto:contact@studiodelemont.ch"
              style={{ fontSize: '12px', color: '#2c2c2c', textDecoration: 'none' }}
            >
              Contact direct (-10%)
            </a>
          </div>
        </div>
      </div>
    </div>
  );
});

// Header Component
const Header = ({ language, onLanguageChange }) => {
  const t = React.useMemo(
    () => TRANSLATIONS[language] || TRANSLATIONS.fr,
    [language]
  );
  const [isBookingModalOpen, setIsBookingModalOpen] = React.useState(false);

  const openBookingModal = React.useCallback(
    () => setIsBookingModalOpen(true),
    []
  );
  const closeBookingModal = React.useCallback(
    () => setIsBookingModalOpen(false),
    []
  );

  return (
    <>
      <header className="header">
        <div className="nav-container">
          {/* Navigation normale pour desktop */}
          <nav className="nav nav-desktop">
            <ul className="nav-menu">
              <li className="nav-item">
                <Link to="/" className="nav-link">
                  {t.nav.home}
                </Link>
              </li>
              <li className="nav-item">
                <Link to="/gallery" className="nav-link">
                  {t.nav.gallery}
                </Link>
              </li>
            </ul>
          </nav>

          {/* Dropdown hamburger pour mobile */}
          <div className="nav-mobile-left">
            <NavigationDropdown t={t} />
          </div>

          <Link to="/" className="logo">
            Studio Delémont
          </Link>

          <div className="header-actions">
            <LanguageDropdown
              currentLang={language}
              onLanguageChange={onLanguageChange}
            />
            <button onClick={openBookingModal} className="book-btn">
              {t.nav.book}
            </button>
          </div>
        </div>
      </header>

      {/* NOUVEAU: Modal Lodgify au lieu de l'ancienne modal */}
      <LodgifyBookingModal
        isOpen={isBookingModalOpen}
        onClose={closeBookingModal}
        language={language}
      />
    </>
  );
};

export default Header;

